// RelatorioCapsulas.js - Relatório de produção de cápsulas
import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Button, Grid, Chip, Autocomplete, Alert, CircularProgress,
  TablePagination
} from '@mui/material';
import {
  Refresh, FilterList, Print
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const RelatorioCapsulas = () => {
  const { api } = useAuth();

  // Estados principais
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dadosProcessados, setDadosProcessados] = useState([]);
  const [totaisDiarios, setTotaisDiarios] = useState({});
  const [totaisDiariosRequisicoes, setTotaisDiariosRequisicoes] = useState({});
  const [estatisticas, setEstatisticas] = useState(null);

  // Estados de filtros
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: '',
    funcionario: ''
  });
  const [opcoesFiltro, setOpcoesFiltro] = useState({
    funcionarios: []
  });
  const [funcionarioSelecionado, setFuncionarioSelecionado] = useState(null);

  // Estados de visualização
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [diasDoMes, setDiasDoMes] = useState([]);

  // Carregar opções de filtro
  const carregarOpcoesFiltro = async () => {
    try {
      const resFuncionarios = await api.listarFuncionarios();

      setOpcoesFiltro({
        funcionarios: resFuncionarios.success ? resFuncionarios.dados : []
      });
    } catch (error) {
      // Erro silencioso
    }
  };

  // Gerar dias do período
  const gerarDiasDoPeriodo = (dataInicio, dataFim) => {
    if (!dataInicio || !dataFim) return [];
    const dias = [];
    const inicio = new Date(`${dataInicio}T00:00:00`);
    const fim = new Date(`${dataFim}T23:59:59`);
    const cursor = new Date(inicio.getTime());
    const nomesSemana = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
    while (cursor <= fim) {
      const ano = cursor.getFullYear();
      const mes = String(cursor.getMonth() + 1).padStart(2, '0');
      const diaNum = String(cursor.getDate()).padStart(2, '0');
      const dataStr = `${ano}-${mes}-${diaNum}`;
      dias.push({
        data: dataStr,
        dia: cursor.getDate(),
        diaSemana: nomesSemana[cursor.getDay()]
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    return dias;
  };

  // Carregar dados do relatório
  const carregarDados = async () => {
    if (!filtros.dataInicio || !filtros.dataFim) {
      setError('Selecione o período (data início e fim)');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const parametros = {
        dataInicio: filtros.dataInicio,
        dataFim: filtros.dataFim
      };

      if (funcionarioSelecionado) parametros.funcionario = funcionarioSelecionado.codigo;

      const res = await api.relatorioCapsulas(parametros);

      if (res.success) {
        const dadosCapsulas = res.dados;

        // Extrair estruturas vindas do backend
        const funcionariosApi = dadosCapsulas.funcionarios || [];
        const totaisPorDiaApi = dadosCapsulas.totais_por_dia || {};
        const totaisPorDiaReqApi = dadosCapsulas.totais_por_dia_requisicoes || {};
        const estatisticasApi = dadosCapsulas.estatisticas || {};

        // Gerar dias do filtro
        const diasGerados = gerarDiasDoPeriodo(filtros.dataInicio, filtros.dataFim);
        const diasValidosSet = new Set(diasGerados.map(d => d.data));

        // Normalizar funcionários
        const funcionariosFormatados = funcionariosApi.map(func => {
          const prodCapsulas = func.producao_diaria || {};
          const prodRequisicoes = func.producao_diaria_requisicoes || {};
          const filtradoCapsulas = Object.fromEntries(
            Object.entries(prodCapsulas).filter(([data]) => diasValidosSet.has(data))
          );
          const filtradoRequisicoes = Object.fromEntries(
            Object.entries(prodRequisicoes).filter(([data]) => diasValidosSet.has(data))
          );
          return {
            codigo: func.codigo,
            nome: func.nome,
            setor: func.setor,
            producaoDiaria: filtradoCapsulas,
            producaoDiariaRequisicoes: filtradoRequisicoes,
            totalCapsulas: func.total_capsulas || 0,
            totalRequisicoes: func.total_requisicoes || 0
          };
        });

        // Recalcular totais por dia
        const totaisFiltrados = diasGerados.reduce((acc, d) => {
          acc[d.data] = funcionariosFormatados.reduce(
            (soma, f) => soma + (f.producaoDiaria[d.data] || 0), 0
          );
          return acc;
        }, {});
        const totaisReqFiltrados = diasGerados.reduce((acc, d) => {
          acc[d.data] = funcionariosFormatados.reduce(
            (soma, f) => soma + (f.producaoDiariaRequisicoes[d.data] || 0), 0
          );
          return acc;
        }, {});

        // Atualizar estados
        setDadosProcessados(funcionariosFormatados);
        setDiasDoMes(diasGerados);
        setTotaisDiarios(totaisFiltrados);
        setTotaisDiariosRequisicoes(totaisReqFiltrados);
        setEstatisticas(estatisticasApi);
      } else {
        setError(res.message || 'Erro ao carregar dados');
      }
    } catch (error) {
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Definir período padrão (mês atual)
  useEffect(() => {
    const hoje = new Date();
    const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);

    setFiltros(prev => ({
      ...prev,
      dataInicio: primeiroDia.toISOString().split('T')[0],
      dataFim: ultimoDia.toISOString().split('T')[0]
    }));

    const diasArray = gerarDiasDoPeriodo(
      primeiroDia.toISOString().split('T')[0],
      ultimoDia.toISOString().split('T')[0]
    );
    setDiasDoMes(diasArray);

    carregarOpcoesFiltro();
  }, []);

  // Aplicar cor baseada na produção de cápsulas
  const obterCorCelula = (valor) => {
    if (valor === 0) return '#ffebee';
    if (valor < 100) return '#fff3e0';
    if (valor < 500) return '#f3e5f5';
    if (valor < 1000) return '#e8f5e8';
    return '#e3f2fd';
  };

  const obterCorTexto = (valor) => {
    if (valor === 0) return '#c62828';
    if (valor < 100) return '#ef6c00';
    if (valor < 500) return '#7b1fa2';
    if (valor < 1000) return '#2e7d32';
    return '#1565c0';
  };

  // Renderizar célula de produção
  const renderCelulaProducao = (valor, data) => {
    const isWeekend = new Date(data).getDay() === 0 || new Date(data).getDay() === 6;
    const temValor = valor > 0;

    return (
      <TableCell
        key={data}
        align="center"
        sx={{
          backgroundColor: obterCorCelula(valor),
          color: obterCorTexto(valor),
          fontWeight: temValor ? 'bold' : 'normal',
          border: '1px solid #e0e0e0',
          minWidth: '80px',
          fontSize: '0.875rem',
          opacity: isWeekend ? 0.7 : 1,
          position: 'relative'
        }}
      >
        {temValor ? valor.toLocaleString('pt-BR') : '-'}
        {isWeekend && (
          <Box
            sx={{
              position: 'absolute',
              top: 2,
              right: 2,
              width: 6,
              height: 6,
              backgroundColor: '#757575',
              borderRadius: '50%'
            }}
          />
        )}
      </TableCell>
    );
  };

  const dadosPaginados = dadosProcessados.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{
      p: 3,
      '@media print': {
        '& .no-print': { display: 'none !important' },
        '& .MuiTableContainer-root': { maxHeight: 'none !important' },
        '& .MuiTable-root': { fontSize: '10px !important' },
        '& .MuiTableCell-root': { padding: '2px !important' }
      }
    }}>

      <Typography variant="h4" gutterBottom>
        Relatório de Produção de Cápsulas
      </Typography>

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 3 }} className="no-print">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Data Início"
              type="date"
              value={filtros.dataInicio}
              onChange={(e) => setFiltros(prev => ({ ...prev, dataInicio: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Data Fim"
              type="date"
              value={filtros.dataFim}
              onChange={(e) => setFiltros(prev => ({ ...prev, dataFim: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={opcoesFiltro.funcionarios}
              getOptionLabel={(option) => option.nome}
              value={funcionarioSelecionado}
              onChange={(event, newValue) => setFuncionarioSelecionado(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Funcionário" fullWidth />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<FilterList />}
                onClick={carregarDados}
                disabled={loading}
              >
                Aplicar Filtros
              </Button>

              <Button
                variant="outlined"
                startIcon={<Print />}
                onClick={() => window.print()}
                disabled={dadosProcessados.length === 0}
              >
                Imprimir
              </Button>

              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => window.location.reload()}
              >
                Limpar Filtros
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Estatísticas resumo */}
      {estatisticas && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {dadosProcessados.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Funcionários
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {estatisticas.total_capsulas?.toLocaleString('pt-BR') || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Cápsulas
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {estatisticas.total_requisicoes?.toLocaleString('pt-BR') || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Requisições
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {estatisticas.media_por_funcionario?.toLocaleString('pt-BR') || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Média por Funcionário
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabela Principal */}
      {dadosProcessados.length > 0 && (
        <Paper>
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      minWidth: '120px',
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      borderRight: '2px solid #e0e0e0'
                    }}
                  >
                    Setor
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                      minWidth: '200px',
                      position: 'sticky',
                      left: '120px',
                      zIndex: 3,
                      borderRight: '2px solid #e0e0e0'
                    }}
                  >
                    Nome do Colaborador
                  </TableCell>

                  {diasDoMes.map(dia => (
                    <TableCell
                      key={dia.data}
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#f5f5f5',
                        minWidth: '60px',
                        fontSize: '0.75rem',
                        padding: '8px 4px'
                      }}
                    >
                      <Box>
                        <Typography variant="caption" display="block">
                          Dia {dia.dia}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {dia.diaSemana}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}

                  <TableCell
                    align="center"
                    sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: '100px' }}
                  >
                    Total Cápsulas
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {dadosPaginados.map((funcionario, index) => (
                  <TableRow
                    key={funcionario.codigo}
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                      '&:hover': { backgroundColor: '#f0f0f0' }
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        border: '1px solid #e0e0e0',
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                        borderRight: '2px solid #e0e0e0',
                        minWidth: '120px'
                      }}
                    >
                      <Chip
                        label={funcionario.setor}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        border: '1px solid #e0e0e0',
                        position: 'sticky',
                        left: '120px',
                        zIndex: 2,
                        backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                        borderRight: '2px solid #e0e0e0',
                        minWidth: '200px'
                      }}
                    >
                      {funcionario.nome}
                    </TableCell>

                    {diasDoMes.map(dia => {
                      const valor = funcionario.producaoDiaria[dia.data] || 0;
                      return renderCelulaProducao(valor, dia.data);
                    })}

                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#e3f2fd',
                        color: '#1565c0',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      {funcionario.totalCapsulas.toLocaleString('pt-BR')}
                    </TableCell>
                  </TableRow>
                ))}

                {/* Linha de totais */}
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', border: '1px solid #e0e0e0' }} colSpan={2}>
                    <Typography variant="h6">TOTAL</Typography>
                  </TableCell>

                  {diasDoMes.map(dia => (
                    <TableCell
                      key={dia.data}
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#e8f5e8',
                        color: '#2e7d32',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      {(totaisDiarios[dia.data] || 0).toLocaleString('pt-BR')}
                    </TableCell>
                  ))}

                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#e3f2fd',
                      color: '#1565c0',
                      border: '1px solid #e0e0e0',
                      fontSize: '1.1rem'
                    }}
                  >
                    {Object.values(totaisDiarios).reduce((a, b) => a + b, 0).toLocaleString('pt-BR')}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginação */}
          <TablePagination
            className="no-print"
            component="div"
            count={dadosProcessados.length}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
          />
        </Paper>
      )}

      {/* Legenda de cores */}
      {dadosProcessados.length > 0 && (
        <Paper sx={{ p: 2, mt: 3 }} className="no-print">
          <Typography variant="h6" gutterBottom>
            Legenda de Cores (Cápsulas)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, backgroundColor: '#ffebee', border: '1px solid #ccc' }} />
                <Typography variant="body2">0 (Sem produção)</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, backgroundColor: '#fff3e0', border: '1px solid #ccc' }} />
                <Typography variant="body2">1-99 (Baixa)</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, backgroundColor: '#f3e5f5', border: '1px solid #ccc' }} />
                <Typography variant="body2">100-499 (Média)</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, backgroundColor: '#e8f5e8', border: '1px solid #ccc' }} />
                <Typography variant="body2">500-999 (Boa)</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, backgroundColor: '#e3f2fd', border: '1px solid #ccc' }} />
                <Typography variant="body2">1000+ (Excelente)</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, backgroundColor: '#757575', borderRadius: '50%' }} />
                <Typography variant="body2">Final de semana</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default RelatorioCapsulas;
